import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useInView, useAnimation, useMotionValue, useSpring } from "framer-motion";
import Lenis from "lenis";

/* ---------------- Smooth scroll ---------------- */
export function useLenis() {
  useEffect(() => {
    // Don't initialize Lenis on admin pages — it hijacks wheel/trackpad
    // events and breaks native overflow-y scroll in the dashboard layout
    if (window.location.pathname.startsWith("/admin")) return;

    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
}

/* ---------------- Custom cursor (Disabled - using native cursor) ---------------- */
export function CustomCursor() {
  return null;
}

/* ---------------- Scroll reveal ---------------- */
export function Reveal({ children, delay = 0, y = 32, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ---------------- Masked line reveal (hero) ---------------- */
export function LineReveal({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/* ---------------- Magnetic button ---------------- */
export function Magnetic({ children, className = "" }) {
  const ref = useRef(null);
  const x = useSpring(useMotionValue(0), { stiffness: 200, damping: 15 });
  const y = useSpring(useMotionValue(0), { stiffness: 200, damping: 15 });
  const handle = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * 0.3);
    y.set((e.clientY - (r.top + r.height / 2)) * 0.3);
  };
  const reset = () => { x.set(0); y.set(0); };
  return (
    <motion.div ref={ref} onMouseMove={handle} onMouseLeave={reset} style={{ x, y }} className={`inline-block ${className}`}>
      {children}
    </motion.div>
  );
}

/* ---------------- Count up ---------------- */
export function CountUp({ end, suffix = "", duration = 1800, decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / duration, 1);
      setVal((1 - Math.pow(1 - p, 3)) * end);
      if (p < 1) requestAnimationFrame(step);
      else setVal(end);
    };
    requestAnimationFrame(step);
  }, [inView, end, duration]);
  return <span ref={ref}>{val.toFixed(decimals)}{suffix}</span>;
}

/* ---------------- Decrypted text animation (cyberpunk / matrix cipher) ---------------- */
const UPPER_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const LOWER_CHARS = "abcdefghijklmnopqrstuvwxyz0123456789";
const SYMBOL_CHARS = "!@#$%^&*?";

function getScrambledChar(originalChar) {
  if (originalChar === " ") return " ";
  if (/[A-Z]/.test(originalChar)) {
    return UPPER_CHARS[Math.floor(Math.random() * UPPER_CHARS.length)];
  }
  if (/[a-z]/.test(originalChar)) {
    return LOWER_CHARS[Math.floor(Math.random() * LOWER_CHARS.length)];
  }
  if (/[0-9]/.test(originalChar)) {
    return UPPER_CHARS[Math.floor(Math.random() * 10)];
  }
  return SYMBOL_CHARS[Math.floor(Math.random() * SYMBOL_CHARS.length)];
}

export function DecryptedText({
  text,
  segments,
  delay = 0,
  speed = 24,
  className = "",
  animateOnHover = true,
}) {
  const normSegments = segments || [{ text: text || "", className: "" }];
  const fullText = normSegments.map((s) => s.text).join("");

  const [displayText, setDisplayText] = useState(fullText);
  const [resolvedIndex, setResolvedIndex] = useState(fullText.length);
  const [isAnimating, setIsAnimating] = useState(false);

  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  const startDecryption = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsAnimating(true);
    setResolvedIndex(0);

    let tick = 0;
    const totalTicks = fullText.length * 2;

    intervalRef.current = setInterval(() => {
      tick += 1;
      const currentResolved = Math.floor(tick / 2);
      setResolvedIndex(currentResolved);

      setDisplayText(
        fullText
          .split("")
          .map((char, i) => {
            if (char === " ") return " ";
            if (i < currentResolved) return char;
            return getScrambledChar(char);
          })
          .join("")
      );

      if (currentResolved >= fullText.length || tick >= totalTicks + 2) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setDisplayText(fullText);
        setResolvedIndex(fullText.length);
        setIsAnimating(false);
      }
    }, speed);
  }, [fullText, speed]);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    // Initialize with scrambled ciphertext on mount
    setDisplayText(fullText.split("").map(getScrambledChar).join(""));
    setResolvedIndex(0);

    timeoutRef.current = setTimeout(() => {
      startDecryption();
    }, delay * 1000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [delay, fullText, startDecryption]);

  let charPointer = 0;

  return (
    <span
      className={`inline-block select-none ${className}`}
      aria-label={fullText}
      onMouseEnter={() => {
        if (animateOnHover && !isAnimating) {
          startDecryption();
        }
      }}
    >
      {normSegments.map((seg, segIdx) => {
        const segLen = seg.text.length;
        const segChars = displayText.slice(charPointer, charPointer + segLen);
        const startIdx = charPointer;
        charPointer += segLen;

        return (
          <span key={segIdx} className={seg.className || undefined}>
            {segChars.split("").map((c, i) => {
              const globalIdx = startIdx + i;
              const isResolved = resolvedIndex >= fullText.length || globalIdx < resolvedIndex;
              const isCurrent = isAnimating && globalIdx === resolvedIndex;

              return (
                <span
                  key={i}
                  className={
                    isCurrent
                      ? "text-[#FF5A1F] drop-shadow-[0_0_8px_rgba(255,90,31,0.8)]"
                      : !isResolved
                      ? "opacity-60"
                      : undefined
                  }
                >
                  {c}
                </span>
              );
            })}
          </span>
        );
      })}
    </span>
  );
}

/* ---------------- Word-by-word reveal (motion writing effect) ---------------- */
export function WordReveal({
  text = "",
  delay = 0.2,
  stagger = 0.035,
  className = "",
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const words = text ? text.split(" ") : [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: stagger,
        delayChildren: delay,
      },
    },
  };

  const wordVariants = {
    hidden: {
      opacity: 0,
      y: 6,
      filter: "blur(4px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <span ref={ref} className={`inline-block ${className}`}>
      <span className="sr-only">{text}</span>
      <motion.span
        aria-hidden="true"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="inline"
      >
        {words.map((word, i) => (
          <motion.span
            key={i}
            variants={wordVariants}
            className="inline-block mr-[0.28em] last:mr-0"
          >
            {word}
          </motion.span>
        ))}
      </motion.span>
    </span>
  );
}

/* ---------------- Typewriter motion effect ---------------- */
export function TypewriterText({
  text = "",
  speed = 25,
  delay = 0.3,
  className = "",
  showCursor = true,
  loop = true,
  pauseDelay = 2000,
  triggerKey = 0,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-60px" });
  const [displayedLength, setDisplayedLength] = useState(0);
  const [status, setStatus] = useState("idle"); // "idle" | "typing" | "paused"

  const pauseTimerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const startTimerRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    if (typingTimerRef.current) clearInterval(typingTimerRef.current);
    if (startTimerRef.current) clearTimeout(startTimerRef.current);
  }, []);

  const startTyping = useCallback(() => {
    clearAllTimers();
    setDisplayedLength(0);
    setStatus("typing");

    typingTimerRef.current = setInterval(() => {
      setDisplayedLength((prev) => {
        if (prev >= text.length) {
          clearInterval(typingTimerRef.current);
          setStatus("paused");
          return text.length;
        }
        return prev + 1;
      });
    }, speed);
  }, [clearAllTimers, speed, text.length]);

  // When section comes inView or triggerKey changes (hover over section)
  useEffect(() => {
    if (!inView) {
      clearAllTimers();
      setStatus("idle");
      return;
    }

    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setDisplayedLength(text.length);
      setStatus("paused");
      return;
    }

    clearAllTimers();
    startTimerRef.current = setTimeout(() => {
      startTyping();
    }, delay * 1000);

    return clearAllTimers;
  }, [inView, triggerKey, delay, clearAllTimers, startTyping, text.length]);

  // Handle loop after 2-second pause
  useEffect(() => {
    if (status !== "paused" || !loop) return;

    pauseTimerRef.current = setTimeout(() => {
      startTyping();
    }, pauseDelay);

    return () => {
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, [status, loop, pauseDelay, startTyping]);

  // Clean up on unmount
  useEffect(() => {
    return clearAllTimers;
  }, [clearAllTimers]);

  const typedPart = text.slice(0, displayedLength);
  const untypedPart = text.slice(displayedLength);

  return (
    <span
      ref={ref}
      className={`inline cursor-default ${className}`}
      onMouseEnter={() => {
        startTyping();
      }}
    >
      <span className="sr-only">{text}</span>
      <span aria-hidden="true" className="inline">
        <span>{typedPart}</span>
        {showCursor && (
          <span
            className={`inline-block w-[2px] h-[1.15em] bg-[#FF5A1F] ml-0.5 align-middle ${
              status === "paused" ? "animate-pulse" : "opacity-100"
            }`}
          />
        )}
        <span className="opacity-0 select-none pointer-events-none">{untypedPart}</span>
      </span>
    </span>
  );
}

/* ---------------- Hero heading multi-line typewriter loop ---------------- */
export function HeroTypewriter({
  speed = 50,
  pauseDelay = 2000,
  delay = 0.2,
  className = "",
}) {
  const line1Plain = "Building ";
  const line1Grad = "AI Solutions";
  const line2Text = "So That You";
  const line3Text = "Don’t Have To!";

  const l1Total = line1Plain.length + line1Grad.length; // 9 + 12 = 21
  const l2Total = line2Text.length; // 11
  const l3Total = line3Text.length; // 14
  const totalChars = l1Total + l2Total + l3Total; // 46

  const [charCount, setCharCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const pauseTimerRef = useRef(null);
  const typingTimerRef = useRef(null);
  const startTimerRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    if (typingTimerRef.current) clearInterval(typingTimerRef.current);
    if (startTimerRef.current) clearTimeout(startTimerRef.current);
  }, []);

  const startTyping = useCallback(() => {
    clearAllTimers();
    setCharCount(0);
    setIsPaused(false);

    typingTimerRef.current = setInterval(() => {
      setCharCount((prev) => {
        if (prev >= totalChars) {
          clearInterval(typingTimerRef.current);
          setIsPaused(true);
          return totalChars;
        }
        return prev + 1;
      });
    }, speed);
  }, [clearAllTimers, speed, totalChars]);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setCharCount(totalChars);
      setIsPaused(true);
      return;
    }

    clearAllTimers();
    startTimerRef.current = setTimeout(() => {
      startTyping();
    }, delay * 1000);

    return clearAllTimers;
  }, [delay, clearAllTimers, startTyping, totalChars]);

  // Handle loop after pauseDelay
  useEffect(() => {
    if (!isPaused) return;

    pauseTimerRef.current = setTimeout(() => {
      setCharCount(0);
      pauseTimerRef.current = setTimeout(() => {
        startTyping();
      }, 300);
    }, pauseDelay);

    return () => {
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
  }, [isPaused, pauseDelay, startTyping]);

  useEffect(() => {
    return clearAllTimers;
  }, [clearAllTimers]);

  // Calculations for Line 1
  const l1Chars = Math.min(charCount, l1Total);
  const l1PlainTypedCount = Math.min(l1Chars, line1Plain.length);
  const l1PlainTyped = line1Plain.slice(0, l1PlainTypedCount);
  const l1PlainUntyped = line1Plain.slice(l1PlainTypedCount);

  const l1GradTypedCount = Math.max(0, l1Chars - line1Plain.length);
  const l1GradTyped = line1Grad.slice(0, l1GradTypedCount);
  const l1GradUntyped = line1Grad.slice(l1GradTypedCount);

  const cursorOnL1 = charCount <= l1Total;

  // Calculations for Line 2
  const l2Chars = Math.max(0, Math.min(charCount - l1Total, l2Total));
  const l2Typed = line2Text.slice(0, l2Chars);
  const l2Untyped = line2Text.slice(l2Chars);
  const cursorOnL2 = charCount > l1Total && charCount <= l1Total + l2Total;

  // Calculations for Line 3
  const l3Chars = Math.max(0, Math.min(charCount - l1Total - l2Total, l3Total));
  const l3Typed = line3Text.slice(0, l3Chars);
  const l3Untyped = line3Text.slice(l3Chars);
  const cursorOnL3 = charCount > l1Total + l2Total;

  const renderCursor = (isActive, isPulsing = false) => {
    if (!isActive) return null;
    return (
      <span
        className={`inline-block w-[3px] sm:w-[4px] lg:w-[5px] h-[0.88em] bg-[#FF5A1F] ml-1 sm:ml-2 align-middle rounded-sm shadow-[0_0_12px_rgba(255,90,31,0.8)] ${
          isPulsing ? "animate-pulse" : "opacity-100"
        }`}
      />
    );
  };

  return (
    <span className={`block select-none cursor-default ${className}`}>
      <span className="sr-only">Building AI Solutions So That You Don’t Have To!</span>
      <span aria-hidden="true" className="block">
        {/* Line 1 */}
        <span className="block">
          <span>{l1PlainTyped}</span>
          <span className="text-gradient">{l1GradTyped}</span>
          {renderCursor(cursorOnL1, false)}
          <span className="opacity-0 pointer-events-none select-none">
            {l1PlainUntyped}
            <span className="text-gradient">{l1GradUntyped}</span>
          </span>
        </span>

        {/* Line 2 */}
        <span className="block">
          <span>{l2Typed}</span>
          {renderCursor(cursorOnL2, false)}
          <span className="opacity-0 pointer-events-none select-none">{l2Untyped}</span>
        </span>

        {/* Line 3 */}
        <span className="block">
          <span>{l3Typed}</span>
          {renderCursor(cursorOnL3, isPaused)}
          <span className="opacity-0 pointer-events-none select-none">{l3Untyped}</span>
        </span>
      </span>
    </span>
  );
}
